import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

const W = 420, H = 594, M = 16;
const K = { a2: 1, a1: Math.SQRT2 };
// draws in A2 millimetres; for A1 every length and font size is scaled up
const SCALE = { text: [1, 2], splitTextToSize: [1], setFontSize: [0], roundedRect: [0, 1, 2, 3, 4, 5], rect: [0, 1, 2, 3], ellipse: [0, 1, 2, 3], line: [0, 1, 2, 3], addImage: [2, 3, 4, 5], setLineWidth: [0], lines: [1, 2] };
const scaled = (d, k) => new Proxy(d, {
  get(tg, prop) {
    const v = tg[prop];
    if (typeof v !== 'function') return v;
    const idx = SCALE[prop];
    if (!idx || k === 1) return v.bind(tg);
    return (...a) => {
      const b = [...a];
      idx.forEach((i) => { if (typeof b[i] === 'number') b[i] *= k; });
      if (prop === 'lines') b[0] = b[0].map((seg) => seg.map((n) => n * k));
      if (prop === 'text' && b[3]?.maxWidth) b[3] = { ...b[3], maxWidth: b[3].maxWidth * k };
      const r = v.apply(tg, b);
      return r === tg ? px : r;
    };
  },
});
let px;
const lat = (s) => String(s || '').replace(/⇄/g, '<->').replace(/[–—]/g, '-').replace(/[^\x20-\xFF]/g, '');

// card with title, subtitle and all leaves; returns height (draw=false only measures)
function card(d, x, y, w, g, style, draw) {
  d.setFont('helvetica', 'bold'); d.setFontSize(15);
  const tl = d.splitTextToSize(lat(g.t), w - 12);
  d.setFont('helvetica', 'normal'); d.setFontSize(9.5);
  const sl = d.splitTextToSize(lat(g.d), w - 12);
  d.setFontSize(10.5);
  const leaves = g.c.map((n) => d.splitTextToSize(lat(n.t), w - 20));
  const h = 8 + tl.length * 6 + sl.length * 4.2 + 3 + leaves.reduce((a, l) => a + l.length * 4.6 + 1.6, 0) + 5;
  if (!draw) return h;
  d.setFillColor(...style.bg); d.setDrawColor(...style.line); d.setLineWidth(0.9); d.roundedRect(x, y, w, h, 5, 5, 'FD');
  let yy = y + 9;
  d.setTextColor(15, 23, 42); d.setFont('helvetica', 'bold'); d.setFontSize(15); d.text(tl, x + 6, yy); yy += tl.length * 6;
  d.setFont('helvetica', 'normal'); d.setFontSize(9.5); d.setTextColor(100, 116, 139); d.text(sl, x + 6, yy); yy += sl.length * 4.2 + 3;
  d.setDrawColor(...style.dash); d.setLineWidth(0.5); d.setLineDashPattern([1.2, 1.2], 0); d.line(x + 7, yy - 2, x + 7, y + h - 5); d.setLineDashPattern([], 0);
  d.setFontSize(10.5);
  leaves.forEach((l, i) => {
    if (g.c[i].k === 'h') { d.setFont('helvetica', 'bold'); d.setTextColor(15, 23, 42); d.text(l, x + 11, yy); d.setFont('helvetica', 'normal'); yy += l.length * 4.6 + 1.6; return; }
    d.setFillColor(...style.leaf); d.ellipse(x + 11, yy - 1.3, 1.9, 1.2, 'F');
    d.setTextColor(20, 83, 45); d.text(l, x + 15, yy); yy += l.length * 4.6 + 1.6;
  });
  return h;
}

function grid(d, list, x0, y0, cols, w, style) {
  const cw = (w - (cols - 1) * 7) / cols;
  let y = y0;
  for (let i = 0; i < list.length; i += cols) {
    const row = list.slice(i, i + cols);
    const rh = Math.max(...row.map((g) => card(d, 0, 0, cw, g, style, false)));
    const off = (cols - row.length) * (cw + 7) / 2;
    row.forEach((g, k) => card(d, x0 + off + k * (cw + 7), y, cw, g, style, true));
    y += rh + 7;
  }
  return y;
}

export async function treePoster({ tree, url, t, size = 'a2' }) {
  const raw = new jsPDF({ unit: 'mm', format: size });
  const d = scaled(raw, K[size]); px = d;
  const G = { bg: [255, 255, 255], line: [34, 197, 94], dash: [134, 239, 172], leaf: [34, 197, 94] };
  const B = { bg: [255, 255, 255], line: [146, 64, 14], dash: [134, 239, 172], leaf: [34, 197, 94] };
  const R = { bg: [255, 251, 235], line: [120, 53, 15], dash: [252, 211, 77], leaf: [217, 119, 6] };
  d.setFillColor(255, 255, 255); d.rect(0, 0, W, H, 'F');
  d.setTextColor(15, 23, 42); d.setFont('helvetica', 'bold'); d.setFontSize(34); d.text('GISS FieldRapport Pro', W / 2, 24, { align: 'center' });
  d.setFont('helvetica', 'normal'); d.setFontSize(15); d.setTextColor(21, 128, 61); d.text(lat(tree.ui.title), W / 2, 33, { align: 'center' });
  const qr = await QRCode.toDataURL(url, { width: 500, margin: 1 });
  d.addImage(qr, 'PNG', W - M - 26, 8, 26, 26);
  d.setFont('helvetica', 'bold'); d.setFontSize(9); d.setTextColor(15, 23, 42); d.text(d.splitTextToSize(lat(t.playTrial7), 40), W - M - 30, 16, { align: 'right' });
  // crown: branches with segments (headings) are split into one card per segment
  const crown = tree.crown.flatMap((g) => (g.c.some((n) => n.k === 'h') ? g.c.reduce((a, n) => { if (n.k === 'h') a.push({ t: n.t, d: g.t, c: [] }); else a[a.length - 1].c.push(n); return a; }, []) : [g]));
  const cc = crown.length > 7 ? 4 : 3;
  const cTop = 46;
  const probe = new jsPDF({ unit: 'mm', format: 'a2' });
  const ch = grid(probe, crown, M + 14, cTop + 16, cc, W - 2 * M - 28, G) - (cTop + 16);
  d.setFillColor(187, 247, 208); d.ellipse(W * 0.25, cTop + 6, 70, 16, 'F'); d.ellipse(W * 0.5, cTop + 2, 80, 16, 'F'); d.ellipse(W * 0.75, cTop + 6, 70, 16, 'F');
  d.setFillColor(220, 252, 231); d.roundedRect(M + 2, cTop, W - 2 * M - 4, ch + 26, 34, 34, 'F');
  d.setFont('helvetica', 'bold'); d.setFontSize(10); d.setTextColor(21, 128, 61); d.text(lat(tree.ui.crown).toUpperCase(), W / 2, cTop + 10, { align: 'center' });
  let y = grid(d, crown, M + 14, cTop + 16, cc, W - 2 * M - 28, G) + 12;
  // trunk
  const tw = 330, tx = (W - tw) / 2;
  const th = grid(probe, tree.trunk, 0, 0, 2, tw - 30, B) + 34;
  d.setFillColor(146, 64, 14); d.rect(tx, y - 10, tw, th, 'F');
  d.setDrawColor(120, 53, 15); d.setLineWidth(0.4);
  for (let x = tx + 8; x < tx + tw; x += 11) d.line(x, y - 10, x + 2, y - 10 + th);
  d.setFillColor(15, 23, 42); d.setDrawColor(253, 224, 71); d.setLineWidth(1.4); d.roundedRect(W / 2 - 60, y - 4, 120, 15, 7.5, 7.5, 'FD');
  d.setTextColor(255); d.setFont('helvetica', 'bold'); d.setFontSize(16); d.text(lat(tree.core), W / 2, y + 6, { align: 'center' });
  d.setFontSize(8.5); d.setTextColor(253, 230, 138); d.text(lat(tree.ui.trunk).toUpperCase(), W / 2, y + 18, { align: 'center' });
  y = grid(d, tree.trunk, tx + 15, y + 23, 2, tw - 30, B);
  y = y - 10 + 3;
  // roots
  d.setDrawColor(146, 64, 14); d.setLineWidth(5); d.setLineCap('round');
  const ry = y + 22;
  const root = (x0, y0, x2, y2) => d.lines([[0, (y2 - y0) * 0.7, x2 - x0, (y2 - y0) * 0.3, x2 - x0, y2 - y0]], x0, y0, [1, 1], 'S', false);
  [W * 0.18, W / 2, W * 0.82].forEach((x2) => root(W / 2, y, x2, ry - 4));
  d.setLineWidth(2); d.setDrawColor(161, 98, 7);
  [W * 0.33, W * 0.67].forEach((x2) => root(W / 2, y + 3, x2, ry - 6));
  d.setFont('helvetica', 'bold'); d.setFontSize(10); d.setTextColor(120, 53, 15); d.text(lat(tree.ui.roots).toUpperCase(), W / 2, ry + 4, { align: 'center' });
  y = grid(d, tree.roots, M, ry + 9, 3, W - 2 * M, R);
  raw.save(`GISS-Funktionsbaum-Poster-${size.toUpperCase()}.pdf`);
}
